import { useState, useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Search, X } from 'lucide-react'
import { listReleases, recordChangelogVisit, type ReleaseSummary, type ReleaseSummaryChange } from '../lib/api'
import { consumeChangelogUtmSource, readChangelogSource } from '../lib/changelogSource'
import { ReleaseListSkeleton } from '../components/ui/Skeleton'
import { cn } from '../lib/utils'

const categoryConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  major: { label: 'Major Release', color: 'text-purple-400', bgColor: 'bg-purple-500/15' },
  feature: { label: 'New Feature', color: 'text-blue-400', bgColor: 'bg-blue-500/15' },
  improvement: { label: 'Improvement', color: 'text-green-400', bgColor: 'bg-green-500/15' },
  bugfix: { label: 'Bug Fix', color: 'text-amber-400', bgColor: 'bg-amber-500/15' },
}

function CategoryPills({ categories }: { categories: string[] }) {
  // Count occurrences of each category
  const counts: Record<string, number> = {}
  for (const cat of categories) {
    counts[cat] = (counts[cat] || 0) + 1
  }

  const order = ['feature', 'improvement', 'major', 'bugfix']
  const sorted = order.filter((c) => counts[c])

  return (
    <div className="flex flex-wrap gap-1.5">
      {sorted.map((cat) => {
        const config = categoryConfig[cat] || {
          label: cat,
          color: 'text-zinc-400',
          bgColor: 'bg-zinc-500/15',
        }
        return (
          <span
            key={cat}
            className={cn('px-2 py-0.5 text-[10px] font-medium rounded-full', config.bgColor, config.color)}
          >
            {counts[cat]} {config.label}{counts[cat] > 1 ? 's' : ''}
          </span>
        )
      })}
    </div>
  )
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export default function Changelog() {
  const [releases, setReleases] = useState<ReleaseSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [categoryFilter, setCategoryFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  // StrictMode double-mounts useEffect in dev. The ref latches the visit
  // record + UTM-strip so we only fire them once per real navigation.
  const visitRecorded = useRef(false)

  useEffect(() => {
    listReleases()
      .then((data) => setReleases(data))
      .catch((err) => setError(err.message || 'Failed to load releases'))
      .finally(() => setLoading(false))

    if (visitRecorded.current) return
    visitRecorded.current = true
    // Fire-and-forget: track that this user opened the changelog index.
    const { source, referrer } = readChangelogSource()
    consumeChangelogUtmSource()
    recordChangelogVisit('index', { source, referrer }).catch(() => {})
  }, [])

  // Collect all unique categories across all releases for filter buttons
  const allCategories = useMemo(() => {
    const cats = new Set<string>()
    for (const r of releases) {
      for (const c of r.change_categories || []) {
        cats.add(c)
      }
    }
    const order = ['feature', 'improvement', 'major', 'bugfix']
    return order.filter((c) => cats.has(c))
  }, [releases])

  const filteredReleases = releases.filter((r) => {
    // Hide releases with no change of the active category. Without the change
    // list rendered inline (below), filtering would otherwise look like a no-op
    // on the user side because most releases contain every category.
    if (categoryFilter) {
      const cats = r.change_categories || []
      if (!cats.includes(categoryFilter)) return false
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      // Search matches release-level fields plus any change title that's
      // visible under the current category filter — so you can refine
      // (filter by category, then search within those changes).
      const matchesRelease =
        r.title.toLowerCase().includes(q) ||
        r.summary.toLowerCase().includes(q) ||
        r.version.toLowerCase().includes(q)
      const visibleChanges = (r.changes || []).filter(
        (c) => !categoryFilter || c.category === categoryFilter
      )
      const matchesChange = visibleChanges.some((c) => c.title.toLowerCase().includes(q))
      return matchesRelease || matchesChange
    }
    return true
  })

  // When a category filter is active, prepare the list of matching change
  // titles per release so each card can render them inline.
  const matchingChangesByVersion = useMemo<Record<string, ReleaseSummaryChange[]>>(() => {
    if (!categoryFilter) return {}
    const out: Record<string, ReleaseSummaryChange[]> = {}
    for (const r of filteredReleases) {
      const matches = (r.changes || []).filter((c) => c.category === categoryFilter)
      if (matches.length > 0) {
        out[r.version] = matches
      }
    }
    return out
  }, [filteredReleases, categoryFilter])

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-bold mb-2">Changelog</h1>
        <p className="text-muted-foreground mb-8">See what's new and improved in Code Concierge.</p>
        <ReleaseListSkeleton count={4} />
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <h1 className="text-2xl font-bold mb-2">Changelog</h1>
        <p className="text-red-400 mt-4">{error}</p>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">Changelog</h1>
      <p className="text-muted-foreground mb-8">See what's new and improved in Code Concierge.</p>

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        {/* Search input */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search releases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-9 py-2 rounded-lg border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Category filter buttons — now filter by highlight-level categories */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setCategoryFilter('')}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-full transition-colors',
              categoryFilter === ''
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-muted-foreground hover:text-foreground'
            )}
          >
            All
          </button>
          {allCategories.map((key) => {
            const config = categoryConfig[key]
            if (!config) return null
            return (
              <button
                key={key}
                onClick={() => setCategoryFilter(categoryFilter === key ? '' : key)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-full transition-colors',
                  categoryFilter === key
                    ? cn(config.bgColor, config.color)
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                )}
              >
                {config.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Release cards or empty state */}
      {filteredReleases.length === 0 ? (
        <div className="text-center py-12">
          {releases.length === 0 ? (
            <p className="text-muted-foreground">No releases yet.</p>
          ) : (
            <div>
              <p className="text-muted-foreground mb-4">No releases match your filters.</p>
              <button
                onClick={() => {
                  setCategoryFilter('')
                  setSearchQuery('')
                }}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredReleases.map((release) => {
            const matches = matchingChangesByVersion[release.version] || []
            const singularLabel = categoryFilter
              ? categoryConfig[categoryFilter]?.label || categoryFilter
              : null
            // 'Bug Fix' pluralizes to 'Bug Fixes', not 'Bug Fixs'.
            const filterLabel =
              matches.length === 1
                ? singularLabel
                : singularLabel === 'Bug Fix'
                  ? 'Bug Fixes'
                  : `${singularLabel}s`
            return (
              <Link
                key={release.version}
                to={`/changelog/${release.version}`}
                className="block rounded-xl border border-border bg-card hover:border-primary/30 transition-colors overflow-hidden"
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Hero image */}
                  <div className="sm:w-64 h-40 sm:h-auto bg-secondary/30 flex-shrink-0">
                    {release.hero_image ? (
                      <img
                        src={release.hero_image}
                        alt={release.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-muted-foreground/30">
                          {release.version}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-5 flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-mono text-muted-foreground">
                        {release.version}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(release.date)}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold mb-1">{release.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {release.summary}
                    </p>
                    {/* Per-item category pills */}
                    {release.change_categories && release.change_categories.length > 0 && (
                      <CategoryPills categories={release.change_categories} />
                    )}
                  </div>
                </div>
                {/* When a category filter is active, surface the matching change
                    titles inline so the filter visibly does something useful. */}
                {categoryFilter && matches.length > 0 && (
                  <div className="border-t border-border bg-secondary/20 px-5 py-3">
                    <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
                      {matches.length} {filterLabel} in this release
                    </div>
                    <ul className="space-y-1">
                      {matches.map((change: ReleaseSummaryChange, i: number) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm text-foreground/80"
                        >
                          <span className="mt-1.5 w-1 h-1 rounded-full bg-muted-foreground/50 flex-shrink-0" />
                          <span>{change.title}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
